import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Factory, Plus, Play, CheckCircle, XCircle, Trash, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fc = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const STATUS_MAP = {
  pendente: { label: "Pendente", color: "bg-amber-100 text-amber-800 border-amber-200" },
  em_producao: { label: "Em Produção", color: "bg-blue-100 text-blue-800 border-blue-200" },
  concluida: { label: "Concluída", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelada: { label: "Cancelada", color: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

export default function ProductionOrders() {
  const [orders, setOrders] = useState([]);
  const { t } = useLanguage();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", scheduled_date: "", notes: "", items: [] });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, sheetsRes] = await Promise.all([
        axios.get(`${API}/production-orders`),
        axios.get(`${API}/technical-sheets`),
      ]);
      setOrders(ordersRes.data);
      setSheets(sheetsRes.data);
    } catch { toast.error("Erro ao carregar dados"); }
    finally { setLoading(false); }
  };

  const toggleSheet = (sheetId) => {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    setForm(prev => {
      const exists = prev.items.find(i => i.sheet_id === sheetId);
      if (exists) return { ...prev, items: prev.items.filter(i => i.sheet_id !== sheetId) };
      return { ...prev, items: [...prev.items, { sheet_id: sheetId, sheet_name: sheet.name, quantity: 1 }] };
    });
  };

  const updateItemQty = (sheetId, value) => {
    const qty = parseInt(value) || 1;
    setForm(prev => ({ ...prev, items: prev.items.map(i => i.sheet_id === sheetId ? { ...i, quantity: Math.max(1, qty) } : i) }));
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.scheduled_date || form.items.length === 0) {
      toast.error("Preencha nome, data e selecione fichas"); return;
    }
    try {
      await axios.post(`${API}/production-orders`, form);
      toast.success("Ordem de produção criada!");
      setDialogOpen(false);
      setForm({ name: "", scheduled_date: "", notes: "", items: [] });
      fetchData();
    } catch { toast.error("Erro ao criar ordem"); }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const msg = newStatus === "concluida" ? "Concluir esta ordem? O estoque será descontado automaticamente." : `Alterar status para "${STATUS_MAP[newStatus]?.label}"?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.put(`${API}/production-orders/${orderId}/status`, { status: newStatus });
      toast.success(`Status atualizado: ${STATUS_MAP[newStatus]?.label}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deletar esta ordem?")) return;
    try { await axios.delete(`${API}/production-orders/${id}`); toast.success("Deletada!"); fetchData(); }
    catch { toast.error("Erro"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" /></div>;

  return (
    <div className="space-y-6" data-testid="production-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("po_title")}</h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">{t("po_subtitle")}</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="add-order-btn">
          <Plus size={16} className="mr-2" /> {t('po_new')}
        </Button>
      </div>

      {orders.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <Factory size={48} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500">{t("po_no_orders")}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => {
            const st = STATUS_MAP[order.status] || STATUS_MAP.pendente;
            return (
              <Card key={order.id} className="border-zinc-200 shadow-sm" data-testid={`order-card-${order.id}`}>
                <CardContent className="py-4 px-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-bold text-zinc-900">{order.name}</p>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${st.color}`}>{st.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-zinc-500">
                        <span className="flex items-center gap-1"><CalendarBlank size={12} />{order.scheduled_date}</span>
                        <span className="font-mono font-semibold text-zinc-700">{fc(order.total_cost)}</span>
                        <span>{order.items.length} ficha(s)</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {order.items.map((item, i) => (
                          <span key={`${order.id}-${i}`} className="text-[10px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">
                            {item.sheet_name} x{item.quantity}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-3 shrink-0">
                      {order.status === "pendente" && (
                        <Button size="sm" variant="outline" className="text-xs h-8 text-blue-700 border-blue-200"
                          onClick={() => handleStatusChange(order.id, "em_producao")} data-testid={`start-order-${order.id}`}>
                          <Play size={14} className="mr-1" /> Iniciar
                        </Button>
                      )}
                      {order.status === "em_producao" && (
                        <Button size="sm" className="text-xs h-8 bg-emerald-700 text-white hover:bg-emerald-800"
                          onClick={() => handleStatusChange(order.id, "concluida")} data-testid={`complete-order-${order.id}`}>
                          <CheckCircle size={14} className="mr-1" /> Concluir
                        </Button>
                      )}
                      {(order.status === "pendente" || order.status === "em_producao") && (
                        <Button size="sm" variant="outline" className="text-xs h-8 text-rose-600 border-rose-200"
                          onClick={() => handleStatusChange(order.id, "cancelada")}>
                          <XCircle size={14} className="mr-1" /> Cancelar
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => handleDelete(order.id)}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('po_new')} de Produção</DialogTitle>
            <DialogDescription className="sr-only">Criar ordem de produção</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Nome *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ex: Produção Segunda" className="mt-1.5" data-testid="order-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Data *</Label>
                <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({...form, scheduled_date: e.target.value})} className="mt-1.5" data-testid="order-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700 mb-2 block">Fichas Técnicas</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {sheets.map(sheet => {
                  const selected = form.items.some(i => i.sheet_id === sheet.id);
                  return (
                    <div key={sheet.id} className={`flex items-center justify-between p-2.5 rounded-lg border transition-colors ${selected ? "border-zinc-950 bg-zinc-50" : "border-zinc-200"}`}>
                      <div className="flex items-center gap-3">
                        <Checkbox checked={selected} onCheckedChange={() => toggleSheet(sheet.id)} />
                        <div>
                          <p className="text-sm font-medium">{sheet.name}</p>
                          <p className="text-xs text-zinc-500">Custo: {fc(sheet.total_cost)}</p>
                        </div>
                      </div>
                      {selected && (
                        <Input type="number" min="1" value={form.items.find(i => i.sheet_id === sheet.id)?.quantity || 1}
                          onChange={(e) => updateItemQty(sheet.id, e.target.value)} className="w-16 h-7 text-center text-sm" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Observações</Label>
              <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} placeholder="Notas..." className="w-full mt-1.5 px-3 py-2 border border-zinc-200 rounded-md text-sm" />
            </div>
            <Button onClick={handleCreate} disabled={form.items.length === 0} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="save-order-btn">
              Criar Ordem
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
